import { SendgridStatus } from "@/utils/constants";
import { logger } from "@/utils/logger";

// Function to determine environment
const getEnvironment = () => {
  const hostname = window.location.hostname;

  if (hostname.includes("staging.lexi.tips")) {
    return "dev";
  } else if (hostname.includes("lexi.tips") && !hostname.includes("staging") && !hostname.includes("dev")) {
    return "prod";
  }

  // Default to dev for localhost and any other domains
  return "dev";
};

// Base URLs with environment placeholder
const CHECK_EMAIL_BASE_URL = "https://lexi-{env}-sendgrid-check-email-on-waitlist-mcdr62tc5a-ts.a.run.app";
const ADD_CONTACT_BASE_URL = "https://lexi-{env}-sendgrid-add-contact-to-waitlist-mcdr62tc5a-ts.a.run.app";

// Get the URLs with the correct environment
const getCheckEmailUrl = () => CHECK_EMAIL_BASE_URL.replace("{env}", getEnvironment());
const getAddContactUrl = () => ADD_CONTACT_BASE_URL.replace("{env}", getEnvironment());

/**
 * Check if an email is already on the SendGrid waitlist
 */
export const checkEmailOnWaitlist = async (email) => {
  try {
    logger.log("Checking if email is on waitlist:", email);

    if (!email) {
      throw new Error("Email is required");
    }

    // Create payload matching the curl command structure exactly
    const payload = {
      email: email,
    };

    // Make API request to check email
    const response = await fetch(getCheckEmailUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    // Handle 404 responses - this means the user is not found, which is not an error
    if (response.status === 404) {
      logger.log("Email not found in SendGrid:", email);
      return {
        success: true,
        isOnWaitlist: false,
        contactData: null,
      };
    }

    // Handle other error responses
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        // Try to parse as JSON, but it might not be
        errorData = JSON.parse(errorText);
      } catch (e) {
        // If it's not valid JSON, use the text directly
        errorData = { error: errorText || "Unknown error" };
      }

      logger.error("SendGrid API error:", errorData);
      throw new Error(
        `SendGrid API error: ${errorData.message || errorData.error || "Unknown error"}`,
      );
    }

    // Parse and process the successful response
    const responseData = await response.json();
    logger.log("Email check response received");

    // Check if the email exists in the response
    const isOnWaitlist = responseData.isOnWaitlist === true;

    return {
      success: true,
      isOnWaitlist,
      contactData: responseData.contactData || null,
    };
  } catch (error) {
    logger.error("Error checking email on waitlist:", error);
    throw error;
  }
};

/**
 * Direct integration with SendGrid API to add contacts to waitlist
 * Using the exact structure from the provided curl command
 */
export const addContactToWaitlist = async (contactData) => {
  const { name, email, mobile, subscription } = contactData;

  try {
    logger.log("Adding contact to waitlist:", { email, subscription });

    // Split the name into first and last name
    const nameParts = name ? name.trim().split(" ") : ["", ""];
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    // Check for existing contact data to preserve subscription status
    let existingSubscriptions = { premium: "", platinum: "" };

    try {
      const checkResult = await checkEmailOnWaitlist(email);
      if (checkResult.success && checkResult.contactData) {
        // Preserve existing subscription values if they exist
        const customFields = checkResult.contactData.custom_fields || {};
        existingSubscriptions.premium =
          customFields.premium === "true" ? "true" : "";
        existingSubscriptions.platinum =
          customFields.platinum === "true" ? "true" : "";
      }
    } catch (error) {
      logger.warn("Could not check existing subscription status:", error);
    }

    // Update with the new subscription
    if (subscription === "Premium") {
      existingSubscriptions.premium = "true";
    } else if (subscription === "VIP") {
      existingSubscriptions.platinum = "true";
    }

    // Create payload without list_ids
    const payload = {
      contacts: [
        {
          email,
          first_name: firstName,
          last_name: lastName,
          phone_number: mobile,
          custom_fields: {
            premium: existingSubscriptions.premium,
            platinum: existingSubscriptions.platinum,
            status: SendgridStatus.PENDING_CONFIRMATION,
          },
        },
      ],
    };

    // Make a single API request
    const response = await fetch(getAddContactUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error("SendGrid API error:", errorData);
      throw new Error(
        `SendGrid API error: ${errorData.message || "Unknown error"}`,
      );
    }

    const responseData = await response.json();
    logger.log("Contact added successfully:", responseData);

    return {
      success: true,
      message: "Contact added to waitlist successfully",
    };
  } catch (error) {
    logger.error("Error adding contact to waitlist:", error);
    throw error;
  }
};

/**
 * Send double opt-in confirmation email
 * @param {string} email - The email address to send confirmation to
 * @returns {Promise<Object>} - Response with success status
 */
export const sendDoubleOptinEmail = async (email) => {
  try {
    logger.log("Sending double opt-in confirmation email to:", email);

    if (!email) {
      throw new Error("Email is required");
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error("Invalid email format");
    }

    // Make API request to send confirmation email
    const response = await fetch("/api/send-double-optin-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      logger.error("API error:", errorData);
      throw new Error(
        errorData.message || errorData.error || "Failed to send confirmation email"
      );
    }

    const responseData = await response.json();
    logger.log("Confirmation email sent successfully:", responseData);

    return {
      success: true,
      message: "Confirmation email sent successfully",
      messageId: responseData.messageId,
    };
  } catch (error) {
    logger.error("Error sending confirmation email:", error);
    throw error;
  }
};
